/**
 * Error block. Screen spec 4.3.
 *
 * Four parts, always in this order, and the last two are never optional:
 *   1. the summary, one sentence, in words a person uses;
 *   2. the plain cause, plus the location as a mono chip on its own line;
 *   3. the actions, with Copy fix request as the screen's one ink block;
 *   4. the raw disclosure, collapsed in Simple, present in Advanced.
 *
 * The fix request is written as a prompt, not as a bug report. It names the
 * product and the situation before it names the error, it carries the code
 * context rather than just the message, and it closes by telling the agent not
 * to deploy. That last line is the most valuable sentence in the payload and
 * it is the kind of thing only the platform knows to say.
 *
 * "What's in this?" sits beside the button and shows the exact payload. Never
 * make anyone paste a black box into an agent.
 */

import { useState } from 'react';
import { AdvancedDisclosure } from './AdvancedDisclosure';
import { Button } from '../primitives/Button';
import { CopyField } from './CopyField';
import { useClipboard } from '../../lib/hooks/use-clipboard';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';
import '../../styles/components/error-block.css';

/**
 * How long the fix request button holds its Copied state. Screen spec 4.3 sets
 * this at 2000ms, longer than the copy field's 1500ms, because this button is
 * the one a person clicks and then switches windows to paste into: the receipt
 * has to still be on screen when they glance back.
 */
export const FIX_COPIED_MS = 2000;

export interface ErrorBlockAction {
  label: string;
  onAct: () => void;
}

export interface ErrorBlockProps {
  summary: string;
  cause: string;
  /** File and line, or the resource that broke. Mono, on its own line. */
  location?: string;
  /** The payload. Written as a prompt, per screen spec 5.6. */
  fixRequest: string;
  /** One context action. Try this deploy again, Try again. Never more. */
  contextAction?: ErrorBlockAction;
  /** The specific disclosure label: Show the build log, Show the stack trace. */
  rawLabel?: string;
  raw?: string;
}

export function ErrorBlock({
  summary,
  cause,
  location,
  fixRequest,
  contextAction,
  rawLabel,
  raw,
}: ErrorBlockProps) {
  const { copy, copied, failed } = useClipboard(FIX_COPIED_MS);
  const [previewOpen, setPreviewOpen] = useState(false);

  async function onCopyFix() {
    const ok = await copy(fixRequest);
    captureEvent('error_fix_request_copied', {
      summary,
      location,
      success: ok,
    });
    // The fallback is not a message, it is the preview with the literal in it.
    if (!ok) setPreviewOpen(true);
  }

  return (
    <section className="errb" aria-label={t('What went wrong')}>
      <p className="errb__summary">{summary}</p>

      <div className="errb__cause">
        <p className="errb__cause-text">{cause}</p>
        {location ? <p className="errb__where mono">{location}</p> : null}
      </div>

      <div className="errb__actions">
        <Button variant="solid" onClick={() => void onCopyFix()}>
          <span className="errb__copy-inner">
            {copied ? (
              <svg viewBox="0 0 16 16" width="14" height="14" focusable="false" aria-hidden="true">
                <path
                  d="M3 8.5 6.5 12 13 4.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : null}
            {copied ? t('Copied') : t('Copy fix request')}
          </span>
        </Button>

        {contextAction ? (
          <Button
            variant="outline"
            onClick={() => {
              captureEvent('error_block_context_action_clicked', {
                label: contextAction.label,
                summary,
              });
              contextAction.onAct();
            }}
          >
            {contextAction.label}
          </Button>
        ) : null}

        <button
          type="button"
          className="errb__whats"
          aria-expanded={previewOpen}
          onClick={() => {
            const next = !previewOpen;
            captureEvent('error_fix_request_preview_toggled', { open: next, summary });
            setPreviewOpen(next);
          }}
        >
          {t("What's in this?")}
        </button>
      </div>

      <span className="errb__say" role="status" aria-live="polite">
        {copied ? t('Fix request copied to your clipboard.') : ''}
      </span>

      {previewOpen ? (
        <div className="errb__preview">
          <p className="errb__preview-label">{t('Paste this to your coding agent')}</p>
          <pre className="errb__fence">{fixRequest}</pre>
          <CopyField label={t('Fix request')} value={fixRequest} size="inline" truncateAt={52} />
          {failed ? (
            <p className="errb__fallback">{t('Copy this and paste it to your coding agent.')}</p>
          ) : null}
        </div>
      ) : null}

      {raw ? (
        <AdvancedDisclosure label={rawLabel ?? t('Show technical details')}>
          <pre className="errb__fence errb__fence--raw">{raw}</pre>
        </AdvancedDisclosure>
      ) : null}
    </section>
  );
}
